// The late-guest watcher on the host's screen (QUEUE row 455). It appears at
// the booked start when the guest is not in the room, says how late they are,
// whether they are knocking, and from five minutes on shows the wording of a
// nudge. It is the host's alone: the Worker only sends it to the proven
// owner's sockets, and it goes away when the guest is let in or at fifteen
// minutes. It also goes the moment anyone knocks or is in the room, which the
// page knows before the Worker's next look (Adam, 2026-10-25: "this needs to
// go away when he enters"), and it sits top left, under an enlarged self view.

import { useEffect, useState, type JSX } from "react";
import type { LateGuestView } from "@breeze/core";

interface LateGuestBannerProps {
  readonly late: LateGuestView | null;
  /** Sends the nudge through the channel the host and guest last wrote on. */
  readonly onSend: (callId: string) => void;
  /** Someone is knocking or already in the call: the knock card or the call
   * itself says so, and this card would only contradict it. */
  readonly arrived: boolean;
}

const buttonStyle = { padding: "8px 16px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer" } as const;

/** Seamless names the channel it used; the host reads it as a word. */
function channelWord(channel: string): string {
  const c = channel.toLowerCase();
  if (c === "text" || c === "imessage" || c === "sms") return "text";
  if (c === "email" || c === "mail") return "email";
  if (c === "linkedin") return "LinkedIn";
  if (c === "whatsapp") return "WhatsApp";
  return channel;
}

function minutesLate(startsAt: number, now: number): number {
  return Math.max(0, Math.floor((now - startsAt) / 60_000));
}

export function LateGuestBanner({ late, onSend, arrived }: LateGuestBannerProps): JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!late) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [late]);

  if (!late || arrived) return null;
  const guest = late.guest || "Your guest";
  const mins = minutesLate(late.startsAt, now);
  const booked = new Date(late.startsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sent = late.send.state === "sent" ? late.send : null;
  const sending = late.send.state === "sending";
  const status = mins < 1 ? "It is time" : `${mins} min late`;

  const copy = (): void => {
    if (!late.nudge) return;
    void navigator.clipboard?.writeText(late.nudge).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => undefined,
    );
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="lateguest"
      style={{
        position: "absolute",
        top: "calc(env(safe-area-inset-top, 0px) + 56px)",
        left: "calc(env(safe-area-inset-left, 0px) + 16px)",
        width: "min(calc(100vw - 32px), 380px)",
        padding: "12px 14px",
        borderRadius: 16,
        background: "rgba(16,21,27,0.86)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        color: "#fff",
        zIndex: 44,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{guest} is not here yet</span>
        <span style={{ fontSize: 13, opacity: 0.75, whiteSpace: "nowrap" }}>{status}</span>
      </div>
      <div style={{ fontSize: 13, opacity: 0.75 }}>
        Booked for {booked}.{" "}
        {late.knocking.length > 0 ? `${late.knocking.join(", ")} ${late.knocking.length === 1 ? "is" : "are"} at the door.` : "No one has knocked."}
      </div>
      {late.nudge ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          <div style={{ fontSize: 14, lineHeight: 1.4, padding: "8px 10px", borderRadius: 10, background: "rgba(255,255,255,0.08)" }}>{late.nudge}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {sent ? (
              <span style={{ fontSize: 14, fontWeight: 600 }}>Sent by {channelWord(sent.channel)}</span>
            ) : (
              <button
                type="button"
                onClick={() => onSend(late.callId)}
                disabled={sending}
                style={{ ...buttonStyle, background: "#fff", color: "#10151b", opacity: sending ? 0.6 : 1 }}
              >
                {sending ? "Sending" : late.send.state === "failed" ? "Try again" : "Send"}
              </button>
            )}
            <button type="button" onClick={copy} style={{ ...buttonStyle, background: "rgba(255,255,255,0.14)", color: "#fff" }}>
              {copied ? "Copied" : "Copy nudge"}
            </button>
          </div>
          {late.send.state === "failed" ? <div style={{ fontSize: 13, opacity: 0.85 }}>{late.send.reason}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
